import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { MarkEmailUnreadOutlined } from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import AuthScreen from 'components/auth/portal/AuthScreen';
import StyledNavigationLink from 'common/theme/links/StyledNavigationLink';
import routes from 'common/routes';
import auth from 'api/graphql/auth';

function RegisterSuccess() {
  const { t } = useTranslation(['auth', 'buttons']);
  const [resendStatus, setResendStatus] = useState('idle');

  const email = sessionStorage.getItem('pendingVerificationEmail') || '';

  const handleResend = async () => {
    if (!email || resendStatus === 'loading') return;
    setResendStatus('loading');
    try {
      const response = await auth.resendVerificationEmail(email);
      const { sent } = response.data.resendVerificationEmail;
      setResendStatus(sent ? 'sent' : 'error');
    } catch {
      setResendStatus('error');
    }
  };

  return (
    <>
      <SEOPageTitle title={t('verifyEmail.title', { ns: 'auth' })} />
      <AuthScreen>
        <Stack spacing={3} useFlexGap sx={{ width: '100%' }}>
          <Stack spacing={1} alignItems="center">
            {/* Ícono (no emoji) en badge neutro, mode-aware. */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'surface.overlay',
                border: '1px solid',
                borderColor: 'hairline.main',
                color: 'gold.main'
              }}>
              <MarkEmailUnreadOutlined sx={{ fontSize: 30 }} />
            </Box>
            <Typography variant="h5" align="center" component="h1" sx={{ fontWeight: 700 }}>
              {t('verifyEmail.title', { ns: 'auth' })}
            </Typography>
            <Typography variant="subtitle2" align="center" sx={{ color: 'text.secondary' }}>
              {t('verifyEmail.subtitle', { ns: 'auth' })}{email ? ` ${email}` : '.'}
            </Typography>
          </Stack>

          {resendStatus === 'sent' && (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              {t('verifyEmail.resendSuccess', { ns: 'auth' })}
            </Alert>
          )}
          {resendStatus === 'error' && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {t('verifyEmail.resendError', { ns: 'auth' })}
            </Alert>
          )}

          <Button
            variant="outlined"
            onClick={handleResend}
            disabled={resendStatus === 'loading' || resendStatus === 'sent'}
            startIcon={resendStatus === 'loading' ? <CircularProgress size={16} /> : null}
            sx={{ borderColor: 'hairline.strong', color: 'text.primary', textTransform: 'none' }}>
            {t('verifyEmail.resendButton', { ns: 'auth' })}
          </Button>

          <StyledNavigationLink
            component={RouterLink}
            to={routes.login.path}
            variant="subtitle2"
            underline="always">
            {t('verifyEmail.loginLink', { ns: 'auth' })}
          </StyledNavigationLink>
        </Stack>
      </AuthScreen>
    </>
  );
}

export default RegisterSuccess;
